<script setup>
import { ref, onMounted } from 'vue';
import { condaApi } from '../condaApi';

const form = ref({ assistantName: '', brandName: '', persona: '' });
const loading = ref(true);
const saving = ref(false);
const saved = ref(false);
const err = ref('');

// The personality prompt is shown read-only; editing happens in a toggled textarea
// working on a draft so Cancel discards cleanly.
const editingHeart = ref(false);
const heartDraft = ref('');

async function load() {
  loading.value = true;
  err.value = '';
  try {
    form.value = await condaApi.getSettings();
  } catch (e) {
    err.value = e.message;
  }
  loading.value = false;
}

async function save() {
  saving.value = true;
  saved.value = false;
  err.value = '';
  try {
    form.value = await condaApi.saveSettings(form.value);
    saved.value = true;
    setTimeout(() => (saved.value = false), 2500);
  } catch (e) {
    err.value = e.message;
  }
  saving.value = false;
}

function startEditHeart() {
  heartDraft.value = form.value.persona || '';
  editingHeart.value = true;
}

async function saveHeart() {
  form.value.persona = heartDraft.value;
  await save();
  if (!err.value) editingHeart.value = false;
}

onMounted(load);
</script>

<template>
  <div class="conda-page">
    <div class="head">
      <h1>Personality</h1>
      <span v-if="saved" class="tag green">Saved</span>
    </div>

    <div v-if="err" class="banner err">{{ err }}</div>
    <div v-if="loading" class="empty">Loading…</div>

    <div v-else class="split">
      <!-- Left: identity -->
      <div class="card" style="height:fit-content">
        <label>Assistant name</label>
        <input v-model="form.assistantName" class="in" placeholder="Ayjay" />

        <label>Brand name</label>
        <input v-model="form.brandName" class="in" placeholder="Too Hot Bank" />

        <div class="muted sm" style="line-height:1.5;margin-top:10px">
          The assistant introduces itself with the <b>name</b> and answers on behalf of the
          <b>brand</b>. These apply instantly to every channel — no redeploy needed.
        </div>

        <div class="row between" style="margin-top:18px">
          <button class="btn primary" :disabled="saving" @click="save">
            {{ saving ? 'Saving…' : 'Save identity' }}
          </button>
          <button class="btn" :disabled="saving" @click="load">Reset</button>
        </div>
      </div>

      <!-- Right: the personality prompt -->
      <div class="card" style="height:fit-content">
        <div class="row between">
          <strong>Personality</strong>
          <button v-if="!editingHeart" class="btn" @click="startEditHeart">Edit</button>
        </div>
        <div class="muted sm" style="line-height:1.5">
          The general prompt behind every answer — it shapes tone, scope and rules. Facts and FAQs
          live in the Knowledge Base and uploaded documents, not here.
        </div>

        <!-- Read-only view -->
        <template v-if="!editingHeart">
          <div
            v-if="form.persona"
            style="margin-top:14px;white-space:pre-wrap;line-height:1.55;font-size:13px;color:#c7ccd6;background:#0f1116;border:1px solid #2a2f3a;border-radius:8px;padding:14px;max-height:52vh;overflow:auto"
          >{{ form.persona }}</div>
          <div v-else class="empty" style="padding:28px">No prompt set yet — click Edit to add one.</div>
        </template>

        <!-- Edit mode -->
        <template v-else>
          <textarea
            v-model="heartDraft"
            class="in"
            style="min-height:300px;margin-top:14px"
            placeholder="e.g. Speak warmly and concisely. Answer banking questions; for anything else, give a one-line polite redirect."
          />
          <div class="row" style="margin-top:12px">
            <button class="btn primary" :disabled="saving" @click="saveHeart">
              {{ saving ? 'Saving…' : 'Save' }}
            </button>
            <button class="btn" :disabled="saving" @click="editingHeart = false">Cancel</button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped src="./conda.css"></style>
